import ActionTextIcon from "@/components/icons/ActionTextIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { useFilterSearchParams } from "@/utils/hooksUtils";
import { URLQuery } from "@/utils/queryUtils";
import { numberOrZero } from "@/utils/utils";
import { Card, Divider, Popconfirm, Table, TablePaginationConfig } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useDeleteProductGroup, useFilterProductGroups } from "../../../../common/api/queries";
import { QueryKeys, TableSizes } from "../../../../common/constants";
import { TopAgentType } from "../../../../common/enums";
import { TabKeyRequest } from "../../../../common/types";
import type { UUID } from "../../../../typings/global";
import { useCurrentAgentAccount } from "../../../auth/hooks";
import { ProductGroup } from "../../types";
import { PRODUCT_PAGE_TABS } from "../../utils";

type Props = {
  onUpdateClick: (group: ProductGroup) => void;
};

export const ProductGroupTableView = ({ onUpdateClick }: Props) => {
  const { t } = useTranslation();

  const userAccount = useCurrentAgentAccount();

  const navigate = useNavigate();
  const filterSearchParams = useFilterSearchParams<TabKeyRequest>({ params: [QueryKeys.TAB] });

  const { data, isLoading } = useFilterProductGroups(
    filterSearchParams,
    filterSearchParams.tab === PRODUCT_PAGE_TABS.PRODUCT_GROUPS,
  );
  const deleteProductGroup = useDeleteProductGroup();

  const handleDeleteProductGroup = async (id: UUID) => {
    await deleteProductGroup.mutateAsync(id);
  };

  const handleTableChange = (pagination: TablePaginationConfig) => {
    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            pageIndex: pagination.current ? pagination.current - 1 : undefined,
          },
          append: true,
        }),
      },
      { replace: true },
    );
  };

  const getColumns = () => {
    const columns: ColumnsType<ProductGroup> = [
      {
        key: "name",
        title: t("product.attrs.name"),
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.name}</Ellipsis>,
      },
    ];

    if (userAccount?.agent?.topAgentType === TopAgentType.PLUS) {
      columns.push({
        key: "name",
        title: t("product.enums.productGroupCategory._label"),
        render: (_, record) =>
          record.category ? (
            <Ellipsis>{t(`product.enums.productGroupCategory.${record.category}`)}</Ellipsis>
          ) : undefined,
      });
    }

    columns.push({
      key: "actions",
      align: "right",
      fixed: "right",
      width: 180,
      render: (_, record) => (
        <>
          <ActionTextIcon icon="edit" color="blue" text={t("common.edit")} onClick={() => onUpdateClick(record)} />

          <Divider orientation="vertical" />

          <Popconfirm
            title={t("product.helpers.productGroupDeleteConfirm")}
            icon={<PopconfirmDeleteIcon />}
            cancelText={t("common.no")}
            okText={t("common.yes")}
            okType="danger"
            onConfirm={() => handleDeleteProductGroup(record.id)}
          >
            <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
          </Popconfirm>
        </>
      ),
    });

    return columns;
  };

  return (
    <Card className="card-box">
      <Table<ProductGroup>
        {...tableStandardProps()}
        loading={isLoading}
        columns={getColumns()}
        scroll={{ x: TableSizes.SMALL }}
        dataSource={data?.pageData}
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(data?.pageIndex) + 1,
          pageSize: data?.pageSize,
          total: data?.totalElementsCount,
        }}
        onChange={handleTableChange}
      />
    </Card>
  );
};
